import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Script from 'next/script';
import { useForm, useWatch } from 'react-hook-form';
import useInitPurchase from '@hooks/useInitPurchase';
import { wrapper } from 'store';
import MetaData from '@/bit/core/core.meta-data';
import { getStaticProps as getStaticPropsMethod } from '@/bit/pages/pages.create-pages';
import getDeviceKindDependsOnViewPort from '@gcloud-npm/utils.common.get-device-kind-depends-on-viewport';
import Saam from '@/bit/core/core.scripts.saam';
import { actions } from '@/reducers/form-mi-movistar-no-client-reducer';
import { actions as addressActions } from '@/reducers/address-reducer';
import PersonalDataView from '@/containers/personalDataView';
import PersonalData from '@/containers/personalData';
import ChooseOperation from '@/containers/chooseOperationArr';
import Shipping from '@/containers/shipping';
import ChooseRentPayment from '@/containers/chooseRentPayment';
import FormLayout from '@/containers/formLayout';
import Address from '@/containers/address';
import Payment from '@/containers/payment';
import { ORDER_SUMMARY } from '@/constants/orderSummary';
import { getAllProductNames } from '@/sagas/miMovistarNoClientForm/utils/formDataUtils';
import { INSTALLATION_ADDRESS } from '@/constants/installationAddress';
import { DELIVERY, OPERATION } from '@/constants/operation';
import { sendTaggingInfo } from '@/utils/common';
import {
  RECAPTCHA_KEY,
  SEGMENT,
  BUBBLE_TEXTS,
  CONFIG_URL,
} from '@/constants/constant';
import {
  PERSONAL_DATA_IDS,
  PATH_NAME,
  URL_SEO,
} from '@/constants/pages/miMovistar';
import FormMiMovistarFake from '@/components/fakes/formPagesFake/formMiMovistarFake';
import {
  calculateShippingText,
  getSimDeliveryText,
  getNextSteps,
  getUpdateNote,
} from '@/utils/orionTextHelper/miMovistar';

const MiMovistar = ({ seoInfo }) => {
  const dispatch = useDispatch();
  const formMiMovistar = useSelector((state) => state.formMiMovistarNoClient);
  const {
    shoppingCart,
    loadingShoppingCart,
    checkout,
    rentPayments,
    lines,
    orionTexts,
    deliveryMethods,
    shippingType,
    hasAFRUpdateNote,
  } = formMiMovistar;

  const [calculatedShippingTexts, setCalculatedShippingTexts] = useState(false);
  const [showErrorBubble, setShowErrorBubble] = useState(false);
  const [savedData, setSavedData] = useState(false);
  const [editingData, setEditingData] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [funnelTaggingInfoSent, setFunnelTaggingInfoSent] = useState(false);
  const [deliveryText, setDeliveryText] = useState(null);
  const [simDeliveryTexts, setSimDeliveryText] = useState(null);
  const [deliveryMthodsWhenLandLine, setDeliveryMethodsWhenLandLine] =
    useState(null);
  const [defaultDeliveryWhenLandLine, setDefaultDeliveryWheLandLine] =
    useState(null);

  const {
    unregister,
    getValues,
    handleSubmit,
    resetField,
    setValue,
    control,
    trigger,
    watch,
  } = useForm({
    mode: 'onTouched',
  });

  const operationFormData = useWatch({ control, name: OPERATION.ID });
  const deliveryValue = useWatch({
    control,
    name: `${DELIVERY.ID}.delivery-choicer.value`,
  });
  const previousDeliveryRef = useRef(null);
  const previousChoiceRef = useRef(null);

  const handleEdit = async () => {
    setEditingData(true);
  };
  const onSubmitSaveData = async () => {
    PERSONAL_DATA_IDS?.GP?.forEach((field) =>
      setValue(field, getValues(field), {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    );
    const isValidPersonalData = await trigger(PERSONAL_DATA_IDS?.GP);
    if (isValidPersonalData) {
      setSavedData(true);
      setEditingData(false);
      setShowErrorBubble(false);
      setSubmitError(false);
      if ('scrollTo' in window && typeof window.scrollTo === 'function') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        });
      }
    }
  };
  const onSubmit = async (formData) => {
    setShowErrorBubble(false);
    const deviceKind = getDeviceKindDependsOnViewPort();
    if (savedData) {
      dispatch(
        actions.checkout({
          formData,
          deviceKind,
          loadingButtonShoppingCart: true,
        })
      );
    } else {
      setSubmitError(true);
    }
  };
  const onError = (formErrors) => {
    if (Object.keys(formErrors).length > 1) {
      setShowErrorBubble(true);
      if ('scrollTo' in window && typeof window.scrollTo === 'function') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        });
      }
    } else if (Object.keys(formErrors).length === 1) {
      setShowErrorBubble(false);
      const scrollElement = document.getElementById(Object.keys(formErrors)[0]);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  };

  useEffect(() => {
    if (Array.isArray(operationFormData)) {
      setCalculatedShippingTexts(
        calculateShippingText({ orionTexts, operationFormData })
      );
      // setDeliveryText(getDeliveryText(orionTexts));
    }
  }, [operationFormData]);

  useEffect(() => {
    if (!Array.isArray(operationFormData)) return;

    const landLine = operationFormData.find(
      (line) => line.type === 'LAND_LINE'
    );

    const choice = landLine?.['action-choicer']?.value;
    const previousChoice = previousChoiceRef.current;

    if (choice === 'PORTABILITY' && previousChoice !== 'PORTABILITY') {
      setDeliveryMethodsWhenLandLine({ SIM: ['SHOP'] });

      previousDeliveryRef.current = deliveryValue;

      if (deliveryValue !== 'SHOP') {
        setValue(`${DELIVERY.ID}.delivery-choicer.value`, 'SHOP', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    }

    if (previousChoice === 'PORTABILITY' && choice !== 'PORTABILITY') {
      setDeliveryMethodsWhenLandLine(null);

      if (previousDeliveryRef.current) {
        setValue(
          `${DELIVERY.ID}.delivery-choicer.value`,
          previousDeliveryRef.current,
          {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          }
        );

        previousDeliveryRef.current = null;
      }
    }

    previousChoiceRef.current = choice ?? null;
  }, [operationFormData, deliveryValue]);

  useEffect(() => {
    setSimDeliveryText(getSimDeliveryText(orionTexts, deliveryValue));
  }, [deliveryValue, orionTexts]);

  useEffect(() => {
    if (savedData) {
      setValue(OPERATION.ID, Array.isArray(lines) ? lines : []);
    }
  }, [savedData]);

  useEffect(() => {
    if (shoppingCart && !funnelTaggingInfoSent) {
      const { totalRecurringPrice } = {
        ...shoppingCart,
      };
      const { finalPrice } = {
        ...totalRecurringPrice,
      };
      sendTaggingInfo(
        {
          product: getAllProductNames(shoppingCart),
          price: finalPrice,
          funnelPurchaseMode: '',
          userType: '',
          funnelStepName: 'formulario',
          transactionType: 'mimovistar',
          funnelFlowName: 'movistar-layer',
        },
        '',
        'movistar_layer_google_ads'
      );
      setFunnelTaggingInfoSent(true);
    }
  }, [shoppingCart]);
  const { setSegment, initPurchase } = actions;

  useInitPurchase({
    segment: SEGMENT.particulares,
    initPurchase,
    setSegment,
    setZone: addressActions.setZone,
    loaded: !!shoppingCart,
  });
  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`}
        strategy="lazyOnload"
      />
      <MetaData seoInfo={seoInfo} />
      <Saam />
      <FormLayout
        onSubmit={handleSubmit(onSubmit, onError)}
        control={control}
        showErrorBubble={showErrorBubble}
        textBubble={BUBBLE_TEXTS.BUBBLE_ERROR_TEXT}
        loaded={!!shoppingCart}
        shoppingCart={{
          ...shoppingCart,
          updateNote:
            getUpdateNote(orionTexts) ||
            (hasAFRUpdateNote ? ORDER_SUMMARY.AFR_UPDATE_NOTE : ''),
        }}
        loadingShoppingCart={loadingShoppingCart}
        loadingButtonShoppingCart={checkout}
        buttonCustomError={
          submitError && ORDER_SUMMARY.BUTTON_CUSTOM_ERROR.MI_MOVISTAR
        }
        formFake={<FormMiMovistarFake />}
        urlLoginCardRedirect={
          !savedData &&
          `${CONFIG_URL}${
            shoppingCart ? `${document.location.search}&directToBuyProcess` : ''
          }`
        }
        nextStepsText={getNextSteps(orionTexts)}
      >
        <>
          {savedData && !editingData && (
            <PersonalDataView
              formData={getValues()}
              edit={handleEdit}
              step={{ label: '1', checked: true }}
              addressType={INSTALLATION_ADDRESS}
              withHorizontalSeparator
            />
          )}
          {(!savedData || editingData) && (
            <>
              <PersonalData
                control={control}
                showTitle
                step={{ label: '1', checked: true }}
              />
              <Address
                unregister={unregister}
                control={control}
                getValues={getValues}
                resetField={resetField}
                componentName={INSTALLATION_ADDRESS.ID}
                formStructure={INSTALLATION_ADDRESS}
                customAction={onSubmitSaveData}
                setInfo={actions.setInfo}
                withHorizontalSeparator
                boldTitle={false}
                setValue={setValue}
              />
            </>
          )}
          <ChooseOperation
            title="Configuración de tus líneas"
            control={control}
            getValues={getValues}
            resetField={resetField}
            lines={lines}
            step={{ label: '2', checked: savedData }}
            showForm={savedData}
            unregister={unregister}
            watch={watch}
            forceHideSimTypes
            withHorizontalSeparator
            shipping={
              <Shipping
                control={control}
                getValues={getValues}
                unregister={unregister}
                resetField={resetField}
                setValue={setValue}
                shippingTitle={calculatedShippingTexts?.title}
                shippingText={calculatedShippingTexts?.description}
                shippingType={calculatedShippingTexts?.shippingType}
                deliveryText={deliveryText}
                deliveryMethods={deliveryMthodsWhenLandLine ?? deliveryMethods}
                showForm={savedData}
                simSelected={calculatedShippingTexts?.simSelected}
                esimText={calculatedShippingTexts?.esimText}
                portabilityText={calculatedShippingTexts?.portabilityText}
                defaultDeliveryMethod={
                  defaultDeliveryWhenLandLine || shippingType
                }
                simDeliveryTexts={simDeliveryTexts}
                showDeliveryComponent
              />
            }
          />

          {rentPayments && (
            <>
              <ChooseRentPayment
                step={{ label: '3', checked: savedData }}
                control={control}
                getValues={getValues}
                action={actions.setInfo}
                rentPayments={rentPayments}
                showForm={savedData}
                withHorizontalSeparator
              />
              <Payment
                step={{ label: '4', checked: savedData }}
                control={control}
                showForm={savedData}
              />
            </>
          )}
          {!rentPayments && (
            <Payment
              step={{ label: '3', checked: savedData }}
              control={control}
              showForm={savedData}
            />
          )}
        </>
      </FormLayout>
    </>
  );
};
export default MiMovistar;

export const getStaticProps = wrapper.getStaticProps(async (data) => {
  return getStaticPropsMethod(data, '', 2, {
    pathname: PATH_NAME,
    orionAlias: '',
    seoLocation: URL_SEO,
    headerLocation: '/',
    layoutInfo: '',
  });
});
